/**
 * Family Store - Zustand
 * Hanterar familjeläget (Premium-funktion)
 * 
 * VIKTIGT: Endast familje-"ägaren" (den som bjuder in) behöver Premium.
 * Inbjudna medlemmar kan ha gratiskonton och får tillgång till ägarens
 * beredskapsdata genom familjelänken.
 */

import { create } from 'zustand';
import { apiPost, apiGet } from '@/services/api-client';
import { logFamilyAction } from '@/lib/activity-logger';

export type FamilyRole = 'owner' | 'member';
export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired';

export interface FamilyMember {
  id: string;
  userId: string; // Koppling till användarens konto
  displayName: string;
  email: string;
  role: FamilyRole;
  joinedAt: string;
  avatarColor?: string;
  isOnline?: boolean;
  lastActive?: string;
}

export interface FamilyInvitation {
  id: string;
  familyId: string;
  familyName: string;
  invitedBy: string; // Ägarens displayName
  invitedByEmail: string;
  inviteeEmail: string;
  status: InvitationStatus;
  createdAt: string;
  expiresAt: string;
  message?: string;
}

export interface Family {
  id: string;
  name: string;
  ownerId: string; // Den som har Premium och bjuder in
  ownerName: string;
  ownerEmail: string;
  ownerPrepGoal?: string;
  ownerHouseholdDisplayName?: string;
  members: FamilyMember[];
  createdAt: string;
  maxMembers: number; // Premium = 5 (inklusive ägaren)
}

interface FamilyState {
  // Familjestatus
  family: Family | null;
  isOwner: boolean;
  
  // Inbjudningar (mottagna)
  pendingInvitations: FamilyInvitation[];
  
  // Skickade inbjudningar (om man är ägare)
  sentInvitations: FamilyInvitation[];
  
  // Loading states
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setFamily: (family: Family | null) => void;
  setIsOwner: (isOwner: boolean) => void;
  setPendingInvitations: (invitations: FamilyInvitation[]) => void;
  setSentInvitations: (invitations: FamilyInvitation[]) => void;
  
  // Familjehantering (för ägare)
  createFamily: (name: string, userId?: string, displayName?: string, email?: string) => Promise<Family | null>;
  inviteMember: (email: string, message?: string) => Promise<FamilyInvitation | null>;
  removeMember: (memberId: string) => void;
  cancelInvitation: (invitationId: string) => void;
  leaveFamily: () => Promise<boolean>;
  
  // Inbjudningshantering (för inbjudna)
  acceptInvitation: (invitationId: string, userId?: string, displayName?: string, email?: string) => Promise<Family | null>;
  declineInvitation: (invitationId: string) => void;
  
  // Hjälpfunktioner
  canInviteMore: () => boolean;
  getMemberCount: () => number;
  getPendingCount: () => number;
  fetchFamily: (familyId: string) => Promise<void>;
  renameFamily?: (newName: string) => Promise<boolean>;
  
  // Hydration
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// Note: helpers removed to avoid unused warnings; add back if needed.

export const useFamilyStore = create<FamilyState>()((set, get) => ({
      family: null,
      isOwner: false,
      pendingInvitations: [],
      sentInvitations: [],
      isLoading: false,
      error: null,

      setFamily: (family) => set({ family }),
      setIsOwner: (isOwner) => set({ isOwner }),
      setPendingInvitations: (invitations) => set({ pendingInvitations: invitations }),
      setSentInvitations: (invitations) => set({ sentInvitations: invitations }),

      createFamily: async (name, _userId, _displayName, _email) => {
        // Call backend to create family (requires Premium)
        const res = await apiPost('/families/create', { name });
        if (res.success && res.data) {
          logFamilyAction('CREATE_FAMILY', (res.data as Family).id, { name });
          set({ family: res.data as Family, isOwner: true });
          // If user's household display name is empty, set it to family name (await and refresh)
          try {
            const authMod = await import('./auth-store');
            const authState = authMod.useAuthStore.getState();
            const current = authState.user;
            if (current && (!current.settings || !current.settings.householdDisplayName)) {
              // best-effort update: await to ensure server persisted value
              await authState.updateUser({ settings: { ...(current.settings || {}), householdDisplayName: (res.data as Family).name } });
              // Refresh full user state from server to pick up familyId/familyRole too
              try { await authState.fetchUser(); } catch (err) { console.error('fetchUser failed', err); }
            }
          } catch (err) {
            console.error('auth-store import/update failed', err);
          }
          return res.data as Family;
        }
        return null;
      },
      
      inviteMember: async (email, message) => {
        const { family, sentInvitations } = get();
        if (!family) return null;
        // Prevent inviting if no slots available (client-side guard)
        const canInvite = get().canInviteMore();
        if (!canInvite) return null;
        // Prevent duplicate pending invite for same email (case-insensitive)
        const emailLower = (email || '').toLowerCase();
        const dup = (sentInvitations || []).some(inv => (inv.inviteeEmail || '').toLowerCase() === emailLower && inv.status === 'pending');
        if (dup) return null;
        const res = await apiPost(`/families/${family.id}/invite`, { inviteeEmail: email, message });
        if (res.success && res.data) {
          logFamilyAction('INVITE_FAMILY_MEMBER', family.id, { inviteeEmail: email });
          const raw = res.data as unknown;
          const r = raw as Record<string, unknown>;
          const invite = {
            id: String(r['id']),
            familyId: String(r['familyId'] ?? family.id),
            familyName: family.name,
            invitedBy: String(r['invitedBy'] || r['invitedByEmail'] || family.ownerName || family.ownerEmail),
            invitedByEmail: String(r['invitedBy'] || r['invitedByEmail'] || family.ownerEmail),
            inviteeEmail: String(r['inviteeEmail'] || r['invitedEmail'] || r['invitedByEmail'] || ''),
            status: String(r['status'] || 'pending'),
            createdAt: String(r['createdAt'] || ''),
            expiresAt: String(r['expiresAt'] || ''),
            message: String(r['message'] || ''),
          } as FamilyInvitation;
          set((s) => ({ sentInvitations: [...s.sentInvitations, invite] }));
          return invite;
        }
        return null;
      },
      
      removeMember: async (memberId) => {
        const { family, isOwner } = get();
        if (!family || !isOwner) return;
        const res = await apiPost(`/families/${family.id}/remove-member`, { userId: memberId });
        if (res.success) {
          logFamilyAction('REMOVE_FAMILY_MEMBER', family.id, { userId: memberId });
          // refresh family
          await (get().fetchFamily?.(family.id) as Promise<void> | undefined);
        } else {
          // optimistic fallback: remove locally
          set({ family: { ...family, members: family.members.filter(m => m.id !== memberId) } });
        }
      },
      
      cancelInvitation: async (invitationId) => {
        const { family } = get();
        if (!family) return;
        const res = await apiPost(`/families/${family.id}/invite/${invitationId}/cancel`);
        if (res.success) {
          await get().fetchFamily(family.id);
        } else {
          set((s) => ({ sentInvitations: s.sentInvitations.filter(inv => inv.id !== invitationId) }));
        }
      },
      
      leaveFamily: async (): Promise<boolean> => {
        const { family } = get();
        if (!family) return false;
        const res = await apiPost(`/families/${family.id}/leave`);
        if (res.success) {
          logFamilyAction('LEAVE_FAMILY', family.id);
          set({ family: null, isOwner: false, sentInvitations: [] });
          return true;
        }
        return false;
      },
      
      acceptInvitation: async (invitationId, _userId, _displayName, _email) => {
        const { pendingInvitations } = get();
        const invitation = pendingInvitations.find(inv => inv.id === invitationId);
        if (!invitation) return null;
        const res = await apiPost(`/families/${invitation.familyId}/accept`, { inviteId: invitationId });
        if (res.success && res.data) {
          logFamilyAction('JOIN_FAMILY', invitation.familyId, { inviteId: invitationId });
          // Fetch family from server to update state
          const familyRes = await apiGet(`/families/${invitation.familyId}`);
          if (familyRes.success && familyRes.data) {
            const familyData = familyRes.data as Family;
            set({ family: familyData, isOwner: false, pendingInvitations: pendingInvitations.filter(inv => inv.id !== invitationId) });
            
            // Clear cached data from previous family and fetch fresh data from new family owner
            try {
              const checklistMod = await import('./checklist-store');
              const checklistState = checklistMod.useChecklistStore.getState();
              // Clear cached checklists
              checklistMod.useChecklistStore.setState({ itemStatus: {}, lastSyncAt: null, hasPendingChanges: false });
              // Fetch fresh checklists from new family owner
              await checklistState.fetchFromServer();
            } catch (err) {
              console.error('Failed to refresh checklists after family join', err);
            }
            
            // Clear and refetch prep test results from new family owner
            try {
              const prepTestMod = await import('./prep-test-store');
              const prepTestState = prepTestMod.usePrepTestStore.getState();
              // Clear cached prep test data
              prepTestMod.usePrepTestStore.setState({ 
                overallScore: null,
                areaScores: [],
                testTakenAt: null,
                rawAnswers: {},
                recommendations: []
              });
              // Fetch fresh prep test from new family owner
              await prepTestState.fetchFromServer(familyData.id);
            } catch (err) {
              console.error('Failed to refresh prep test after family join', err);
            }
            
            // If user's householdDisplayName is empty, set it to family name
            try {
              const authMod = await import('./auth-store');
              const authState = authMod.useAuthStore.getState();
              const current = authState.user;
              if (current && (!current.settings || !current.settings.householdDisplayName)) {
                await authState.updateUser({ settings: { ...(current.settings || {}), householdDisplayName: familyData.name } });
                try { await authState.fetchUser(); } catch (err) { console.error('fetchUser failed', err); }
              }
            } catch (err) {
              console.error('auth-store import/update failed', err);
            }
            return familyRes.data as Family;
          }
        }
        return null;
        },
      
      declineInvitation: async (invitationId) => {
        const { pendingInvitations } = get();
        let invitation = pendingInvitations.find(inv => inv.id === invitationId);

        // If we don't have the invitation locally, try fetching "my invites" from server
        if (!invitation) {
          try {
            const res = await apiGet('/families/invites/mine');
            if (res.success && res.data) {
              const rawArr = res.data as unknown;
              const arr = (rawArr as unknown[]) || [];
              const remote = arr.map((r) => {
                const rec = r as Record<string, unknown>;
                return {
                  id: String(rec['id']),
                  familyId: String(rec['familyId'] || ''),
                  familyName: String(rec['familyName'] || ''),
                  invitedBy: String(rec['invitedBy'] || rec['invitedByEmail'] || ''),
                  invitedByEmail: String(rec['invitedByEmail'] || ''),
                  inviteeEmail: String(rec['inviteeEmail'] || rec['invitedEmail'] || ''),
                  status: String(rec['status'] || 'pending'),
                  createdAt: String(rec['createdAt'] || ''),
                  expiresAt: String(rec['expiresAt'] || ''),
                  message: String(rec['message'] || ''),
                } as FamilyInvitation;
              });
              // Update local pendingInvitations with remote data
              set({ pendingInvitations: remote.filter(i => i.status === 'pending') });
              invitation = remote.find(i => i.id === invitationId) || undefined;
            }
          } catch (err) {
            console.error('fetch user failed', err);
          }
        }

        if (!invitation) return;

        try {
          await apiPost(`/families/${invitation.familyId}/decline`, { inviteId: invitationId });
        } catch (err) {
          console.error('Decline API failed', err);
        }

        set((s) => ({ pendingInvitations: s.pendingInvitations.filter(inv => inv.id !== invitationId) }));
      },
      
      canInviteMore: () => {
        const { family, sentInvitations } = get();
        if (!family) return false;
        
        const currentMembers = family.members.length;
        const pendingInvites = sentInvitations.filter(inv => inv.status === 'pending').length;
        
        return (currentMembers + pendingInvites) < family.maxMembers;
      },
      
      getMemberCount: () => {
        const { family } = get();
        return family?.members.length || 0;
      },
      
      getPendingCount: () => {
        const { sentInvitations } = get();
        return sentInvitations.filter(inv => inv.status === 'pending').length;
      },
      fetchFamily: async (familyId: string) => {
        if (!familyId) return;
        const res = await apiGet(`/families/${familyId}`);
        if (res.success && res.data) {
          const data = res.data as unknown as Record<string, unknown>;
          const rawInvites = (data['invites'] as unknown) || [];
          const invites = (rawInvites as unknown[]).map((raw) => {
            const r = raw as Record<string, unknown>;
            return {
              id: String(r['id']),
              familyId: String(data['id'] || ''),
              familyName: String(data['name'] || ''),
              invitedBy: String(r['invitedBy'] || data['ownerName'] || data['ownerEmail'] || ''),
              invitedByEmail: String(data['ownerEmail'] || ''),
              inviteeEmail: String(r['inviteeEmail'] || r['invitedEmail'] || ''),
              status: String(r['status'] || 'pending'),
              createdAt: String(r['createdAt'] || ''),
              expiresAt: String(r['expiresAt'] || ''),
              message: String(r['message'] || ''),
            } as FamilyInvitation;
          }) as FamilyInvitation[];

          // Determine whether current user is the owner by consulting auth-store.
          let isOwnerFlag = false;
          try {
            const authMod = await import('./auth-store');
            const current = authMod.useAuthStore.getState().user;
            const ownerId = String(data['ownerId'] || '');
            const ownerEmail = String(data['ownerEmail'] || '');
            if (current && (current.id === ownerId || current.email === ownerEmail)) {
              isOwnerFlag = true;
            }
          } catch (err) {
            // best-effort: if we cannot import auth-store just leave isOwner false
            console.warn('fetchFamily: failed to determine owner', err);
          }

          set({ family: data as unknown as Family, isOwner: isOwnerFlag, pendingInvitations: (invites.filter((i: FamilyInvitation) => i.status === 'pending')), sentInvitations: invites });
        }
      },

          // Rename family (owner only)
          renameFamily: async (newName: string) => {
            const { family, isOwner } = get();
            if (!family || !isOwner) return false;
            try {
              const res = await apiPost(`/families/${(family as Family).id}/rename`, { name: newName });
              if (res && typeof res.success === 'boolean' && res.success && res.data) {
                const data = res.data as { id?: string; name?: string };
                logFamilyAction('UPDATE_FAMILY', (family as Family).id, { newName });
                set({ family: { ...(family as Family), name: String(data.name || newName) } });
                return true;
              }
            } catch (err) {
              console.error('renameFamily failed', err);
            }
            return false;
          },
      
      _hasHydrated: true, // No persistence, so always hydrated
      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }));

// Helper hooks
export const useFamilyHydrated = () => useFamilyStore((state) => state._hasHydrated);
export const useIsInFamily = () => useFamilyStore((state) => state.family !== null);
export const useIsFamilyOwner = () => useFamilyStore((state) => state.isOwner);

// Helper: get the effective family owner id to use when fetching family-scoped data.
// Returns owner's userId when the current user is a family member, otherwise undefined.
// Return the effective family id to use when requesting owner-scoped data.
// Previously this returned the owner's userId which was incorrect — the
// backend expects the `familyId` (the family's `id`) when passed as
// `?familyId=`. Keep the function name for compatibility with callers.
export const getEffectiveFamilyOwnerId = () => {
  const s = useFamilyStore.getState();
  if (!s.family) return undefined;
  return s.family.id || undefined;
};
